import { useCallback, useRef } from 'react';

export function useDebounceCallback<
  Arguments extends unknown[],
  Return extends void
>(
  method: (...args: Arguments) => Return,
  delay = 500
): (...args: Arguments) => void {
  const argsRef = useRef<Arguments | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  function removePendingTimer() {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }

  const debouncedCallback = useCallback(
    (...args: Arguments) => {
      // capture latest args
      argsRef.current = args;

      // clear debounce timer
      removePendingTimer();

      // start waiting again
      timeout.current = setTimeout(() => {
        if (argsRef.current) {
          method(...argsRef.current);
        }
      }, delay);
    },
    [delay, method]
  );

  return debouncedCallback;
}
